import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, RefreshCw, ExternalLink, ArrowLeft } from 'lucide-react';
import { useAppSelector } from '../store';
import { useActiveEvent } from '../contexts/EventContext';
import { API_BASE_URL } from '../lib/utils';
import { usePermission } from '../components/shared/PermissionGuard';
import type { PatientReportForm } from '../types/prf';

type StoredPRF = Partial<PatientReportForm> & {
  id: string;
  incidentId: string;
  eventId?: string;
  submittedAt?: string;
  createdBy?: string;
  data?: Partial<PatientReportForm>;
};

export default function PRFListPage() {
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId } = useActiveEvent();
  const [prfs, setPrfs] = useState<StoredPRF[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const canView = usePermission('prfs:view');

  const fetchPrfs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const url = activeEventId
        ? `${API_BASE_URL}/prfs?eventId=${activeEventId}`
        : `${API_BASE_URL}/prfs`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPrfs(data.prfs || []);
      }
    } catch (e) {
      console.error('Failed to fetch PRFs', e);
    } finally {
      setLoading(false);
    }
  }, [token, activeEventId]);

  useEffect(() => {
    if (canView) fetchPrfs();
  }, [fetchPrfs, canView]);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Access Denied</h2>
          <p className="text-sm text-muted-foreground">You do not have permission to view Patient Report Forms.</p>
        </div>
      </div>
    );
  }

  const filtered = prfs.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const patient = p.data?.patientDetails || p.patientDetails || {};
    const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.toLowerCase();
    return (
      p.id.toLowerCase().includes(q) ||
      p.incidentId.toLowerCase().includes(q) ||
      fullName.includes(q)
    );
  });

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const getPatientName = (p: StoredPRF) => {
    const pd = p.data?.patientDetails || p.patientDetails || {};
    const name = `${pd.firstName || ''} ${pd.lastName || ''}`.trim();
    return name || '(unnamed)';
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="p-1.5 rounded-md hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <FileText className="w-5 h-5 text-primary" />
            <div>
              <h1 className="text-base font-bold text-foreground">Patient Report Forms</h1>
              <p className="text-xs text-muted-foreground">{filtered.length} {filtered.length === 1 ? 'report' : 'reports'}</p>
            </div>
          </div>
          <button
            onClick={fetchPrfs}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent hover:bg-accent/80 transition-colors text-xs font-medium"
            disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by PRF ref, incident ID or patient name..."
            className="w-full h-9 pl-9 pr-3 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                {prfs.length === 0 ? 'No Patient Report Forms submitted yet' : 'No PRFs match your search'}
              </p>
            </div>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr className="text-left text-muted-foreground">
                <th className="px-4 py-2 font-semibold">PRF Reference</th>
                <th className="px-4 py-2 font-semibold">Patient</th>
                <th className="px-4 py-2 font-semibold">Incident</th>
                <th className="px-4 py-2 font-semibold">Submitted</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((prf) => (
                <tr
                  key={prf.id}
                  onClick={() => navigate(`/prfs/${encodeURIComponent(prf.id)}`)}
                  className="hover:bg-accent/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 font-mono text-foreground">{prf.id}</td>
                  <td className="px-4 py-2.5 text-foreground">{getPatientName(prf)}</td>
                  <td className="px-4 py-2.5 font-mono text-muted-foreground">{prf.incidentId}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{formatDate(prf.submittedAt)}</td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-500/20 text-green-400">
                      Submitted
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
